import React, { useEffect, useState, useCallback } from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import { triggerDefs } from "./types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface RoutingHistoryItem {
  id: number;
  conversationId: number | null;
  occurredAt: string;
  trigger: string;
  ruleId: number | null;
  ruleName: string | null;
  outcomeSource: string;
  outcomeType: string;
  outcomeId: number | null;
  outcomeLabel: string | null;
  conditionsEvaluated: number;
}

interface RoutingHistoryPage {
  total: number;
  page: number;
  pageSize: number;
  items: RoutingHistoryItem[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<string, string> = triggerDefs as Record<string, string>;

const OUTCOME_TYPE_LABELS: Record<string, string> = {
  survey_shown: "Pre-Chat Survey",
  routed_to_team: "Routed to Team",
  workflow_started: "Workflow Started",
  unavailable_survey_shown: "Unavailable Survey",
  postchat_survey_shown: "Post-Chat Survey",
  no_match: "No Match"
};

const OUTCOME_COLORS: Record<string, string> = {
  survey_shown: "bg-purple-100 text-purple-800",
  routed_to_team: "bg-blue-100 text-blue-800",
  workflow_started: "bg-emerald-100 text-emerald-800",
  unavailable_survey_shown: "bg-amber-100 text-amber-800",
  postchat_survey_shown: "bg-pink-100 text-pink-800",
  no_match: "bg-slate-100 text-slate-500"
};

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  rule_match: { label: "Rule", color: "bg-cornflower-blue-100 text-cornflower-blue-800" },
  deployment_default: { label: "Default", color: "bg-slate-100 text-slate-600" },
  no_match: { label: "No Match", color: "bg-red-50 text-red-600" }
};

const DAY_OPTIONS = [
  { label: "Last 24 hours", value: 1 },
  { label: "Last 7 days", value: 7 },
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 }
];

const PAGE_SIZE = 50;

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

function OutcomeBadge({ type }: { type: string }) {
  const color = OUTCOME_COLORS[type] ?? "bg-slate-100 text-slate-500";
  const label = OUTCOME_TYPE_LABELS[type] ?? type;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const def = SOURCE_LABELS[source] ?? { label: source, color: "bg-slate-100 text-slate-500" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${def.color}`}>
      {def.label}
    </span>
  );
}

function TriggerLabel({ trigger }: { trigger: string }) {
  const label = TRIGGER_LABELS[trigger] ?? trigger;
  return (
    <span className="text-sm text-slate-700 font-medium" title={trigger}>
      {label}
    </span>
  );
}

// ── Stats Strip ───────────────────────────────────────────────────────────────

function StatsStrip({ items, total }: { items: RoutingHistoryItem[]; total: number }) {
  const ruleMatches = items.filter((i) => i.outcomeSource === "rule_match").length;
  const noMatches = items.filter((i) => i.outcomeType === "no_match").length;

  if (total === 0) return null;

  return (
    <div className="flex items-center gap-6 py-2 px-1 text-sm text-slate-500 border-b border-slate-100">
      <span>
        <strong className="text-slate-800">{total}</strong> event{total !== 1 ? "s" : ""} found
      </span>
      <span>
        <strong className="text-cornflower-blue-700">{ruleMatches}</strong> rule matches
        {ruleMatches < total && ` (on this page)`}
      </span>
      <span>
        <strong className="text-slate-600">{noMatches}</strong> no-match
        {noMatches < total && ` (on this page)`}
      </span>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function RoutingHistory() {
  const api = useApi();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RoutingHistoryPage | null>(null);
  const [availableTriggers, setAvailableTriggers] = useState<string[]>([]);

  // Filters
  const [days, setDays] = useState(7);
  const [triggerFilter, setTriggerFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [ruleSearch, setRuleSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        days: String(days),
        page: String(page),
        pageSize: String(PAGE_SIZE)
      });
      if (triggerFilter) params.set("trigger", triggerFilter);
      if (outcomeFilter) params.set("outcomeType", outcomeFilter);

      const response = await api.messaging.get(`RoutingHistory?${params}`);
      const json = await response.json();
      setData({
        total: json?.total ?? 0,
        page: json?.page ?? 1,
        pageSize: json?.pageSize ?? PAGE_SIZE,
        items: Array.isArray(json?.items) ? json.items : []
      });
    } catch {
      setData({ total: 0, page: 1, pageSize: PAGE_SIZE, items: [] });
    } finally {
      setLoading(false);
    }
  }, [api, days, triggerFilter, outcomeFilter, page]);

  // Fetch available triggers for dropdown (once, on mount)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.messaging.get("RoutingHistory/Triggers?days=30");
        const json = await res.json();
        setAvailableTriggers(Array.isArray(json) ? json : []);
      } catch {
        setAvailableTriggers([]);
      }
    })();
  }, [api]);

  useEffect(() => {
    setPage(1); // reset to page 1 when filters change
  }, [days, triggerFilter, outcomeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Client-side rule name filter (fast, no extra API call)
  const visibleItems = ruleSearch.trim()
    ? (data?.items ?? []).filter((i) =>
        (i.ruleName ?? "").toLowerCase().includes(ruleSearch.toLowerCase())
      )
    : (data?.items ?? []);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Routing History</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            A log of every routing decision made for your site — which rule fired, why, and what happened.
          </p>
        </div>
        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {DAY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Trigger filter */}
        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300 min-w-[180px]"
          value={triggerFilter}
          onChange={(e) => setTriggerFilter(e.target.value)}
        >
          <option value="">All trigger types</option>
          {availableTriggers.map((t) => (
            <option key={t} value={t}>
              {TRIGGER_LABELS[t] ?? t}
            </option>
          ))}
        </select>

        {/* Outcome filter */}
        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300 min-w-[160px]"
          value={outcomeFilter}
          onChange={(e) => setOutcomeFilter(e.target.value)}
        >
          <option value="">All outcomes</option>
          {Object.entries(OUTCOME_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        {/* Rule name search */}
        <input
          type="text"
          placeholder="Filter by rule name…"
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300 min-w-[200px] flex-1"
          value={ruleSearch}
          onChange={(e) => setRuleSearch(e.target.value)}
        />

        {/* Clear filters */}
        {(triggerFilter || outcomeFilter || ruleSearch) && (
          <button
            className="text-sm text-slate-400 hover:text-slate-600 px-2"
            onClick={() => {
              setTriggerFilter("");
              setOutcomeFilter("");
              setRuleSearch("");
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Stats strip */}
      {!loading && data && (
        <StatsStrip items={data.items} total={data.total} />
      )}

      {/* Table */}
      <div className="mt-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : visibleItems.length === 0 ? (
          <EmptyState hasFilters={!!(triggerFilter || outcomeFilter || ruleSearch)} days={days} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 font-semibold">Time</th>
                  <th className="px-4 py-3 font-semibold">Conversation</th>
                  <th className="px-4 py-3 font-semibold">Trigger</th>
                  <th className="px-4 py-3 font-semibold">Rule Matched</th>
                  <th className="px-4 py-3 font-semibold">Source</th>
                  <th className="px-4 py-3 font-semibold">Outcome</th>
                  <th className="px-4 py-3 font-semibold">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleItems.map((item) => (
                  <RoutingHistoryRow key={item.id} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
          <span>
            Showing {(data.page - 1) * PAGE_SIZE + 1}–{Math.min(data.page * PAGE_SIZE, data.total)} of{" "}
            {data.total}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className="px-2">
              Page {page} of {totalPages}
            </span>
            <button
              className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-3 text-xs text-slate-400">
        <span className="font-medium text-slate-500">Outcomes:</span>
        {Object.entries(OUTCOME_TYPE_LABELS).map(([k, v]) => (
          <span key={k} className={`px-2 py-0.5 rounded-full font-medium ${OUTCOME_COLORS[k] ?? "bg-slate-100 text-slate-500"}`}>
            {v}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Row component ─────────────────────────────────────────────────────────────

function RoutingHistoryRow({ item }: { item: RoutingHistoryItem }) {
  return (
    <tr className="hover:bg-slate-50 transition-colors">
      {/* Time */}
      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
        {formatTime(item.occurredAt)}
      </td>

      {/* Conversation */}
      <td className="px-4 py-3">
        {item.conversationId ? (
          <span className="font-mono text-xs text-cornflower-blue-700 bg-cornflower-blue-50 px-1.5 py-0.5 rounded">
            #{item.conversationId}
          </span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>

      {/* Trigger */}
      <td className="px-4 py-3">
        <TriggerLabel trigger={item.trigger} />
      </td>

      {/* Rule matched */}
      <td className="px-4 py-3">
        {item.ruleName ? (
          <span className="text-sm text-slate-700">
            {item.ruleName}
            {item.ruleId && (
              <span className="ml-1 text-xs text-slate-400">#{item.ruleId}</span>
            )}
          </span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>

      {/* Source */}
      <td className="px-4 py-3">
        <SourceBadge source={item.outcomeSource} />
      </td>

      {/* Outcome type */}
      <td className="px-4 py-3">
        <OutcomeBadge type={item.outcomeType} />
      </td>

      {/* Outcome label */}
      <td className="px-4 py-3">
        {item.outcomeLabel ? (
          <span className="text-sm text-slate-600">{item.outcomeLabel}</span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>
    </tr>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

function EmptyState({ hasFilters, days }: { hasFilters: boolean; days: number }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <svg
          className="w-6 h-6 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      </div>
      {hasFilters ? (
        <>
          <p className="text-slate-600 font-medium">No routing events match your filters</p>
          <p className="text-slate-400 text-sm mt-1">Try widening the date range or clearing filters</p>
        </>
      ) : (
        <>
          <p className="text-slate-600 font-medium">No routing events in the last {days} day{days !== 1 ? "s" : ""}</p>
          <p className="text-slate-400 text-sm mt-1 max-w-sm">
            Routing decisions are logged when rules evaluate conversations. Activity will appear
            here once chats come in.
          </p>
        </>
      )}
    </div>
  );
}
